import { forwardRef, useState } from "react";
import { ChevronDown, Calendar, Building2, Filter, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { AMOUNT_RANGES, amountRangeLabel } from "@/lib/amount-ranges";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useDepartments,
  useProvinces,
  useDistricts,
} from "@/hooks/queries/useDepartments";
import { useProcuringEntities } from "@/hooks/queries/useProcuringEntities";
import { STAGE_LIST, SUBJECT_LIST } from "@/lib/domain-constants";
import { formatDate } from "@/lib/format";
import dayjs from "dayjs";

/**
 * Parse any date/ISO string (`YYYY-MM-DD` or full ISO) to a local Date,
 * matching how the pill formats it and avoiding UTC off-by-one in the calendar.
 */
function parseDateInput(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const d = dayjs(value);
  if (!d.isValid()) return undefined;
  return new Date(d.year(), d.month(), d.date());
}

/** Format a Date back to the `YYYY-MM-DD` string the filters store. */
function toDateString(date?: Date): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Tamaño de la contratación: las licitaciones del SEACE grande frente a las
 * contrataciones menores (<=8 UIT). Viven en la misma tabla y se distinguen por
 * `contracting_minorid`; el selector deja verlas juntas (por defecto), solo unas
 * o solo las otras.
 */
export type ContractingScope = "all" | "major" | "minor";

export interface FilterValues {
  departmentIds: number[];
  provinceIds: number[];
  districtIds: number[];
  dateFrom: string;
  dateTo: string;
  entityIds: number[];
  subjects: string[];
  stages: string[];
  maxDays: number | null;
  onlyInterests: boolean;
  /** Opcional a proposito: sin valor equivale a "all", y asi los inicializadores
   *  que ya existian no tienen que cambiar. */
  scope?: ContractingScope;
  /** Rango de monto en soles; opcional por la misma razón que `scope`. */
  amountMin?: number | null;
  amountMax?: number | null;
}

export const PillTrigger = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    label: string;
    value: string;
    icon?: React.ReactNode;
    active?: boolean;
    disabled?: boolean;
  }
>(({ label, value, icon, active, disabled, className, ...props }, ref) => (
  <button
    ref={ref}
    disabled={disabled}
    {...props}
    className={cn(
      "flex items-center gap-2 rounded-sm border bg-card px-3 py-2 text-xs transition-colors cursor-pointer",
      active
        ? "border-primary/30 bg-primary/5 text-primary/80"
        : "hover:bg-accent/30",
      disabled && "cursor-not-allowed opacity-50 hover:bg-card",
      className,
    )}
  >
    <span
      className={cn(
        "font-medium",
        active ? "text-primary/70" : "text-slate-400",
      )}
    >
      {label}
    </span>
    <span
      className={cn("font-bold", active ? "text-primary" : "text-foreground")}
    >
      {value}
    </span>

    <span className={cn(active ? "text-primary/70" : "text-slate-400")}>
      {icon ?? <ChevronDown className="size-3.5" />}
    </span>
  </button>
));
PillTrigger.displayName = "PillTrigger";

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseDateInput(value);
  const currentYear = new Date().getFullYear();
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between rounded-sm border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent",
              !selected && "text-muted-foreground",
            )}
          >
            {selected ? formatDate(value) : "Seleccionar fecha"}
            <Calendar className="size-4 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <CalendarPicker
            mode="single"
            captionLayout="dropdown"
            startMonth={new Date(2015, 0)}
            endMonth={new Date(currentYear + 1, 11)}
            defaultMonth={selected}
            selected={selected}
            onSelect={(date) => {
              onChange(toDateString(date));
              if (date) setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FilterHeader({
  title,
  showClear,
  onClear,
}: {
  title: string;
  showClear?: boolean;
  onClear?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {showClear && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="cursor-pointer text-xs font-semibold text-primary hover:underline"
        >
          Limpiar
        </button>
      )}
    </div>
  );
}

/** Lista con casillas para elegir varios; también la usa el Mercado de Fianzas para el lugar. */
export function CheckboxList<T extends string | number>({
  title,
  options,
  selected,
  onToggle,
  onClear,
}: {
  title: string;
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
  onClear?: () => void;
}) {
  return (
    <div className="space-y-2">
      <FilterHeader
        title={title}
        showClear={selected.length > 0}
        onClear={onClear}
      />
      <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto pr-1">
        {options.map((o) => (
          <div
            key={String(o.value)}
            role="button"
            tabIndex={0}
            onClick={() => onToggle(o.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle(o.value);
              }
            }}
            className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-left hover:bg-accent"
          >
            <Checkbox
              checked={selected.includes(o.value)}
              className="pointer-events-none"
            />
            <span className="text-sm">{o.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SCOPE_OPTIONS: { value: ContractingScope; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "major", label: "Licitaciones grandes" },
  { value: "minor", label: "Contrataciones menores" },
];

const CONTRATADO_OPTIONS = [
  { value: null, label: "Todos" },
  { value: 7, label: "Últimos 7 días" },
  { value: 15, label: "Últimos 15 días" },
  { value: 30, label: "Últimos 30 días" },
  { value: 60, label: "Últimos 60 días" },
  { value: 90, label: "Últimos 90 días" },
];

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

export function FilterBar({
  value,
  onChange,
  showProvinceDistrict = false,
  showEntity = true,
  showStages = true,
  showContratado = true,
  showOnlyInterests = true,
  showScope = false,
  showAmount = false,
}: {
  value: FilterValues;
  onChange: (patch: Partial<FilterValues>) => void;
  showProvinceDistrict?: boolean;
  showEntity?: boolean;
  showStages?: boolean;
  showContratado?: boolean;
  showOnlyInterests?: boolean;
  /**
   * Opt-in, a diferencia del resto de flags: `scope` solo llega al backend en
   * las pantallas que consultan licitaciones (`MyContractings`, `ProfileCard`).
   * Companies y TDM arman sus propios params y lo descartarían, así que ahí el
   * selector sería un control que no hace nada.
   */
  showScope?: boolean;
  /** Opt-in como `scope`: solo las pantallas que mandan el monto al backend. */
  showAmount?: boolean;
}) {
  const { data: departments = [] } = useDepartments();
  const [entitySearch, setEntitySearch] = useState("");
  const { data: entities = [] } = useProcuringEntities(entitySearch);

  const {
    departmentIds,
    provinceIds,
    districtIds,
    dateFrom,
    dateTo,
    entityIds,
    subjects,
    stages,
    maxDays,
    onlyInterests,
    scope = "all",
    amountMin = null,
    amountMax = null,
  } = value;

  // Cascade: province options depend on a single selected department,
  // district options on a single selected province.
  const singleDept = departmentIds.length === 1 ? departmentIds[0] : undefined;
  const singleProv = provinceIds.length === 1 ? provinceIds[0] : undefined;
  const { data: provinces = [] } = useProvinces(singleDept);
  const { data: districts = [] } = useDistricts(singleProv);

  function multiLabel(
    ids: number[],
    options: { id: number; name: string }[],
    allLabel: string,
  ) {
    if (ids.length === 0) return allLabel;
    if (ids.length === 1) {
      return options.find((o) => o.id === ids[0])?.name ?? "1 sel.";
    }
    return `${ids.length} sel.`;
  }

  const deptOptions = departments.map((d) => ({
    id: d.department_id,
    name: d.department_name,
  }));
  const provOptions = provinces.map((p) => ({
    id: p.province_id,
    name: p.province_name,
  }));
  const distOptions = districts.map((d) => ({
    id: d.district_id,
    name: d.district_name,
  }));

  const dateValue =
    dateFrom || dateTo
      ? `${dateFrom ? formatDate(dateFrom) : "…"} → ${dateTo ? formatDate(dateTo) : "…"}`
      : "Todo el tiempo";
  const contratadoLabel =
    CONTRATADO_OPTIONS.find((o) => o.value === maxDays)?.label ?? "Todos";

  return (
    <div className="flex items-center gap-2">
      <span className="mr-1 text-xs font-bold uppercase tracking-wide text-slate-400">
        Filtros
      </span>

      <div className="flex flex-wrap items-center gap-2">
        {/* Departamento */}
        <Popover>
          <PopoverTrigger asChild>
            <PillTrigger
              label="Departamento"
              value={multiLabel(departmentIds, deptOptions, "Todos")}
              active={departmentIds.length > 0}
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64">
            <CheckboxList
              title="Departamentos"
              options={departments.map((d) => ({
                value: d.department_id,
                label: d.department_name,
              }))}
              selected={departmentIds}
              onToggle={(v) =>
                onChange({
                  departmentIds: toggle(departmentIds, v),
                  provinceIds: [],
                  districtIds: [],
                })
              }
              onClear={() =>
                onChange({
                  departmentIds: [],
                  provinceIds: [],
                  districtIds: [],
                })
              }
            />
          </PopoverContent>
        </Popover>

        {/* Provincia / Distrito (cascade) — only shown when exactly one
          department / province is selected. */}
        {showProvinceDistrict && singleDept != null && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Provincia"
                value={multiLabel(provinceIds, provOptions, "Todas")}
                active={provinceIds.length > 0}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64">
              <CheckboxList
                title="Provincias"
                options={provOptions.map((o) => ({
                  value: o.id,
                  label: o.name,
                }))}
                selected={provinceIds}
                onToggle={(v) =>
                  onChange({
                    provinceIds: toggle(provinceIds, v),
                    districtIds: [],
                  })
                }
                onClear={() => onChange({ provinceIds: [], districtIds: [] })}
              />
            </PopoverContent>
          </Popover>
        )}

        {showProvinceDistrict && singleProv != null && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Distrito"
                value={multiLabel(districtIds, distOptions, "Todos")}
                active={districtIds.length > 0}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64">
              <CheckboxList
                title="Distritos"
                options={distOptions.map((o) => ({
                  value: o.id,
                  label: o.name,
                }))}
                selected={districtIds}
                onToggle={(v) =>
                  onChange({ districtIds: toggle(districtIds, v) })
                }
                onClear={() => onChange({ districtIds: [] })}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Fecha */}
        <Popover>
          <PopoverTrigger asChild>
            <PillTrigger
              label="Fecha"
              value={dateValue}
              icon={<Calendar className="size-3.5" />}
              active={!!(dateFrom || dateTo)}
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-3">
            <FilterHeader
              title="Fecha convocatoria"
              showClear={!!(dateFrom || dateTo)}
              onClear={() => onChange({ dateFrom: "", dateTo: "" })}
            />
            <DateField
              label="Desde (Convocatoria)"
              value={dateFrom}
              onChange={(v) => onChange({ dateFrom: v })}
            />
            <DateField
              label="Hasta (Convocatoria)"
              value={dateTo}
              onChange={(v) => onChange({ dateTo: v })}
            />
          </PopoverContent>
        </Popover>

        {/* Entidad */}
        {showEntity && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Entidad"
                value={entityIds.length ? `${entityIds.length} sel.` : "Todas"}
                icon={<Building2 className="size-3.5" />}
                active={entityIds.length > 0}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 space-y-2">
              <FilterHeader
                title="Entidades"
                showClear={entityIds.length > 0}
                onClear={() => onChange({ entityIds: [] })}
              />
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  placeholder="Buscar entidad..."
                  className="pl-8"
                />
              </div>
              <div className="flex max-h-56 flex-col gap-0.5 overflow-y-auto pr-1">
                {entitySearch.length < 2 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground">
                    Escribe al menos 2 letras para buscar.
                  </p>
                ) : entities.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground">
                    Sin resultados.
                  </p>
                ) : (
                  entities.map((e) => (
                    <div
                      key={e.procuringentity_id}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        onChange({
                          entityIds: toggle(entityIds, e.procuringentity_id),
                        })
                      }
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          onChange({
                            entityIds: toggle(entityIds, e.procuringentity_id),
                          });
                        }
                      }}
                      className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-left hover:bg-accent"
                    >
                      <Checkbox
                        checked={entityIds.includes(e.procuringentity_id)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm">{e.procuringentity_name}</span>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Etapas */}
        {showStages && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Etapas"
                value={stages.length ? `${stages.length} sel.` : "Todas"}
                active={stages.length > 0}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64">
              <CheckboxList
                title="Etapas"
                options={STAGE_LIST.map((s) => ({
                  value: s.key,
                  label: s.label,
                }))}
                selected={stages}
                onToggle={(v) => onChange({ stages: toggle(stages, v) })}
                onClear={() => onChange({ stages: [] })}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Objeto */}
        <Popover>
          <PopoverTrigger asChild>
            <PillTrigger
              label="Objeto"
              value={subjects.length ? `${subjects.length} sel.` : "Todos"}
              active={subjects.length > 0}
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56">
            <CheckboxList
              title="Objeto"
              options={SUBJECT_LIST.map((s) => ({
                value: s.key,
                label: s.label,
              }))}
              selected={subjects}
              onToggle={(v) => onChange({ subjects: toggle(subjects, v) })}
              onClear={() => onChange({ subjects: [] })}
            />
          </PopoverContent>
        </Popover>

        {/* Tamaño: licitaciones grandes vs contrataciones <=8 UIT */}
        {showScope && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Tamaño"
                value={
                  SCOPE_OPTIONS.find((o) => o.value === scope)?.label ?? "Todas"
                }
                active={scope !== "all"}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-52">
              <div className="flex flex-col gap-0.5">
                {SCOPE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => onChange({ scope: o.value })}
                    className={cn(
                      "rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                      scope === o.value && "font-semibold text-primary",
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Días contratado */}
        {showContratado && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Días Contratado"
                value={contratadoLabel}
                active={maxDays != null}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-52">
              <div className="flex flex-col gap-0.5">
                {CONTRATADO_OPTIONS.map((o) => (
                  <button
                    key={o.label}
                    onClick={() => onChange({ maxDays: o.value })}
                    className={cn(
                      "rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                      maxDays === o.value && "font-semibold text-primary",
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Monto: el oficial y, si no hay, el leído de las bases */}
        {showAmount && (
          <Popover>
            <PopoverTrigger asChild>
              <PillTrigger
                label="Monto"
                value={amountRangeLabel(amountMin, amountMax)}
                active={amountMin != null || amountMax != null}
              />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-52">
              <div className="flex flex-col gap-0.5">
                {[{ min: null, max: null, label: "Cualquiera" }, ...AMOUNT_RANGES].map(
                  (range) => (
                    <button
                      key={range.label}
                      onClick={() =>
                        onChange({ amountMin: range.min, amountMax: range.max })
                      }
                      className={cn(
                        "cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                        amountMin === range.min &&
                          amountMax === range.max &&
                          "font-semibold text-primary",
                      )}
                    >
                      {range.label}
                    </button>
                  ),
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Solo intereses */}
        {showOnlyInterests && (
          <button
            onClick={() => onChange({ onlyInterests: !onlyInterests })}
            className={cn(
              "flex items-center gap-2 rounded-sm border px-3 py-2 text-xs font-medium transition-colors cursor-pointer",
              onlyInterests
                ? "border-primary/30 bg-primary/5 text-primary/70"
                : "bg-card text-muted-foreground hover:bg-accent",
            )}
          >
            <Filter className="size-3.5" />
            Ver solo mis intereses
          </button>
        )}
      </div>
    </div>
  );
}
